"use client";

import { useEffect, useState, useCallback } from "react";
import { useFansUnitedSDK } from "fansunited-data-layer/client";
import { useAuth } from "@/lib/auth/AuthContext";
import type { ProfileData, Stats } from "../types";
interface UseProfileDataReturn {
  profileData: ProfileData | null;
  stats: Stats | null;
  isLoading: boolean;
  reloadProfile: () => Promise<void>;
}
export function useProfileData(): UseProfileDataReturn {
  const {
    user,
    loading: authLoading
  } = useAuth();
  const sdk = useFansUnitedSDK();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const loadProfile = useCallback(async () => {
    if (!sdk || !user || user.isAnonymous) return;
    try {
      const sdkProfile = await sdk.profile.getOwn().getInfo();
      setProfileData({
        name: sdkProfile.name || user.displayName || "",
        email: sdkProfile.email || user.email || "",
        avatar: sdkProfile.avatar || user.photoURL || undefined,
        nickname: sdkProfile.nickname || undefined,
        country: sdkProfile.country || null,
        gender: sdkProfile.gender || undefined
      });
    } catch (err) {
      console.error("Failed to load profile:", err);
      setProfileData({
        name: user.displayName || "",
        email: user.email || "",
        avatar: user.photoURL || undefined
      });
    }
  }, [sdk, user]);
  useEffect(() => {
    if (!sdk || !user || user.isAnonymous) {
      if (!authLoading && user && !user.isAnonymous && !sdk) {
        setProfileData({
          name: user.displayName || "",
          email: user.email || "",
          avatar: user.photoURL || undefined
        });
        setIsLoading(false);
      }
      return;
    }
    const fetch = async () => {
      setIsLoading(true);
      try {
        const [, statsResult] = await Promise.all([loadProfile(), sdk.profile.getOwnStats()]);
        setStats(statsResult as unknown as Stats);
      } catch (err_0) {
        console.error("Failed to load profile data:", err_0);
      } finally {
        setIsLoading(false);
      }
    };
    fetch();
  }, [sdk, user, authLoading, loadProfile]);
  return {
    profileData,
    stats,
    isLoading,
    reloadProfile: loadProfile
  };
}